import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { translations } from "./translations";

const STORAGE_KEY = "heartstream.language";
const DEFAULT_LANGUAGE = "en";
const LANGUAGE_OPTIONS = [
  { code: "en", label: "English", shortLabel: "EN", locale: "en-US" },
  { code: "sk", label: "Slovenčina", shortLabel: "SK", locale: "sk-SK" },
];
const SUPPORTED_LANGUAGES = LANGUAGE_OPTIONS.map((option) => option.code);

const LanguageContext = createContext(null);

function inferLanguage() {
  if (typeof window === "undefined") return DEFAULT_LANGUAGE;

  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (SUPPORTED_LANGUAGES.includes(stored)) return stored;

  const browserLanguage = window.navigator.language?.toLowerCase() || "";
  return browserLanguage.startsWith("sk") ? "sk" : DEFAULT_LANGUAGE;
}

function getValue(dictionary, key) {
  return key.split(".").reduce((value, part) => value?.[part], dictionary);
}

function interpolate(value, vars = {}) {
  return value.replace(/\{(\w+)\}/g, (_, name) => {
    const replacement = vars[name];
    return replacement == null ? "" : String(replacement);
  });
}

export function getLocale(language) {
  return LANGUAGE_OPTIONS.find((option) => option.code === language)?.locale || "en-US";
}

export function formatDateTime(value, language) {
  if (!value) return "-";
  return new Date(value).toLocaleString(getLocale(language));
}

export function formatDate(value, language) {
  if (!value) return "-";
  return new Date(`${value}T00:00:00`).toLocaleDateString(getLocale(language));
}

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(inferLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
    window.localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const value = useMemo(() => {
    const dictionary = translations[language] || translations[DEFAULT_LANGUAGE];

    const t = (key, vars) => {
      const translated = getValue(dictionary, key) ?? getValue(translations[DEFAULT_LANGUAGE], key) ?? key;
      return typeof translated === "string" ? interpolate(translated, vars) : translated;
    };

    return {
      language,
      languages: SUPPORTED_LANGUAGES,
      languageOptions: LANGUAGE_OPTIONS,
      setLanguage: (nextLanguage) => {
        if (SUPPORTED_LANGUAGES.includes(nextLanguage)) setLanguageState(nextLanguage);
      },
      t,
    };
  }, [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used inside LanguageProvider");
  return context;
}

export function useTranslation() {
  return useLanguage().t;
}
